/**
 * Sidebar §57-58: ширина 220-240px, основное пространство отдано контенту.
 */
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus, ShieldCheck } from 'lucide-react';
import { walletApi } from '../../api/wallet';
import { Button } from '../ui/Button';
import { personaNav, primaryNav, secondaryNav, type NavItem } from './navigation';
import { UserMenu } from './UserMenu';
import { useAuthStore } from '../../stores/auth';
import { formatNumber } from '../../i18n/format';

function NavRow({
  item,
  onNavigate,
  trailing,
}: {
  item: NavItem;
  onNavigate?: () => void;
  trailing?: string;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 rounded-control px-3 py-2 text-body transition-colors duration-fast',
          isActive
            ? 'bg-surface-hover text-content'
            : 'text-content-secondary hover:bg-surface-hover hover:text-content',
        )
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
      <span className="truncate">{t(item.labelKey)}</span>
      {trailing ? <span className="ml-auto text-meta text-content-muted">{trailing}</span> : null}
    </NavLink>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // §58: баланс виден числом прямо в навигации, без перехода в раздел.
  const user = useAuthStore((state) => state.user);
  const balance = useQuery({
    queryKey: ['wallet'],
    queryFn: walletApi.balance,
    staleTime: 30_000,
  });

  return (
    <div className="flex h-full flex-col gap-6 border-r border-line bg-surface px-4 py-5">
      <NavLink to="/" onClick={onNavigate} className="px-2 text-card-title font-semibold tracking-tight">
        Alterium
      </NavLink>

      {/* §63: единственная очевидная primary action в шелле */}
      <Button
        variant="primary"
        onClick={() => {
          navigate('/create');
          onNavigate?.();
        }}
      >
        <Plus className="h-4 w-4" strokeWidth={2} />{t('nav.createVideo')}</Button>

      <nav className="flex flex-1 flex-col gap-1">
        {primaryNav.map((item) => (
          <NavRow key={item.to} item={item} onNavigate={onNavigate} />
        ))}

        <div className="my-2 h-px bg-line" />

        {personaNav.map((item) => (
          <NavRow key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="flex flex-col gap-1">
        <div className="my-1 h-px bg-line" />
        {secondaryNav.map((item) => (
          <NavRow
            key={item.to}
            item={item}
            onNavigate={onNavigate}
            {...(item.to === '/balance' && balance.data
              ? { trailing: formatNumber(balance.data.available) }
              : {})}
          />
        ))}
        {/* §51: вход в админку виден только администратору. */}
        {user?.role === 'admin' ? (
          <NavRow
            item={{ to: '/admin', labelKey: 'nav.admin', icon: ShieldCheck }}
            onNavigate={onNavigate}
          />
        ) : null}
        <div className="my-1 h-px bg-line" />
        <UserMenu />
      </div>
    </div>
  );
}
